import type { AppRouter } from "@luckymenu/api/router";
import { HEADERS, TRPC_MAX_BATCH } from "@luckymenu/core";
import { createTRPCClient, httpBatchLink, type TRPCClient } from "@trpc/client";

export type { AppRouter };
export type ApiClient = TRPCClient<AppRouter>;

export type ApiClientOptions = {
	/**
	 * The tRPC endpoint. Same origin `/api/trpc` for browsers, and
	 * `https://api.<root>/api/trpc` for the Android app. See api.md.
	 */
	url: string;
	/** A fresh Clerk session token, or null when signed out. Called for every request. */
	getToken: () => Promise<string | null>;
	/** The chosen restaurant, sent as `x-restaurant-id`. */
	getRestaurantId?: () => string | null;
	/** An admin support-session token, sent as `x-impersonation`. Staff web only. */
	getSupportToken?: () => string | null;
	/** Sent as `x-app-version`. */
	appVersion?: string;
	/** The Android build number, sent as `x-app-build`: the API refuses builds below its minimum (P6-T13). */
	appBuild?: number;
	/** A quick switch token (P6-T11), sent as `x-staff-switch`. Staff apps only. */
	getStaffSwitchToken?: () => Promise<string | null> | string | null;
	/**
	 * This install's device ID, sent as `x-device-id` (staff apps): print jobs
	 * a call makes give this device their 2 second head start (printing.md).
	 */
	getDeviceId?: () => Promise<string | null> | string | null;
	fetch?: typeof fetch;
};

function requestId(): string {
	return (
		globalThis.crypto?.randomUUID?.() ??
		`${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
	);
}

/** The typed tRPC client used by staff web, staff Android and admin. */
export function createApiClient(options: ApiClientOptions): ApiClient {
	return createTRPCClient<AppRouter>({
		links: [
			httpBatchLink({
				url: options.url,
				maxURLLength: 2000,
				maxItems: TRPC_MAX_BATCH,
				...(options.fetch ? { fetch: options.fetch } : {}),
				async headers() {
					const headers: Record<string, string> = { [HEADERS.requestId]: requestId() };
					const token = await options.getToken();
					if (token) headers.authorization = `Bearer ${token}`;
					const restaurantId = options.getRestaurantId?.();
					if (restaurantId) headers[HEADERS.restaurantId] = restaurantId;
					const support = options.getSupportToken?.();
					if (support) headers[HEADERS.impersonation] = support;
					if (options.appVersion) headers[HEADERS.appVersion] = options.appVersion;
					if (options.appBuild) headers[HEADERS.appBuild] = String(options.appBuild);
					const staffSwitch = await options.getStaffSwitchToken?.();
					if (staffSwitch) headers[HEADERS.staffSwitch] = staffSwitch;
					const deviceId = await options.getDeviceId?.();
					if (deviceId) headers[HEADERS.deviceId] = deviceId;
					return headers;
				},
			}),
		],
	});
}
